import { format, formatDistanceToNow } from "date-fns";

import { es } from "date-fns/locale";

import { Badge } from "@/components/ui/badge";

import { Card } from "@/components/ui/card";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import EmptyState from "../../../shared/components/EmptyState";

import LoadingOverlay from "../../../shared/components/LoadingOverlay";

import PageHeader from "../../../shared/components/PageHeader";

import { useSubscriptionsQuery } from "../hooks/useSubscriptions";

import type { SubscriptionStatus } from "../types";

const SubscriptionsPage = () => {
  const { data, isLoading, isFetching } = useSubscriptionsQuery();

  const subscriptions = data?.data ?? [];

  const isBusy = isLoading || isFetching;

  const getStatusBadge = (status: SubscriptionStatus) => {
    switch (status) {
      case "pending":
        return (
          <Badge className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30">
            Pendiente
          </Badge>
        );

      case "active":
        return (
          <Badge className="bg-green-500/20 text-green-400 hover:bg-green-500/30">
            Activo
          </Badge>
        );

      case "expired":
        return (
          <Badge className="bg-neutral-500/20 text-neutral-400 hover:bg-neutral-500/30">
            Expirado
          </Badge>
        );

      case "cancelled":
        return (
          <Badge className="bg-red-500/20 text-red-400 hover:bg-red-500/30">
            Cancelado
          </Badge>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Suscripciones"
        subtitle="Suscripciones de usuarios a planes"
      />

      <Card className="border-neutral-800 bg-neutral-900/50">
        <Table>
          <TableHeader>
            <TableRow className="border-neutral-800 hover:bg-transparent">
              <TableHead className="text-neutral-400">Usuario</TableHead>
              <TableHead className="text-neutral-400">Plan</TableHead>
              <TableHead className="text-neutral-400">Pago</TableHead>
              <TableHead className="text-neutral-400">Periodo</TableHead>
              <TableHead className="text-neutral-400">Estado</TableHead>
              <TableHead className="text-neutral-400">Fecha</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subscriptions.map((sub) => (
              <TableRow
                key={sub.id}
                className="border-neutral-800 hover:bg-neutral-800/50"
              >
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium text-neutral-100">
                      {sub.user
                        ? `${sub.user.name || ""} ${sub.user.lastName || ""}`.trim() ||
                          sub.user.username ||
                          `User #${sub.userId}`
                        : `User #${sub.userId}`}
                    </span>
                    {sub.user?.email && (
                      <span className="text-xs text-neutral-500">
                        {sub.user.email}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col text-sm text-neutral-300">
                    <span className="font-medium">{sub.plan?.name || "—"}</span>
                    <span className="text-xs text-neutral-500 capitalize">
                      {sub.plan?.type || "—"} · ${sub.plan?.monthlyPrice || 0}
                      /mes
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {sub.paymentAmountUsdt ? (
                    <div className="flex flex-col text-sm text-neutral-300">
                      <span>
                        ${Number(sub.paymentAmountUsdt).toFixed(2)} USDT
                      </span>
                      {sub.txHash && (
                        <a
                          href={`https://polygonscan.com/tx/${sub.txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-violet-400 hover:underline"
                        >
                          {sub.txHash.slice(0, 10)}...
                        </a>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-neutral-500">Sin pago</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col text-sm text-neutral-300">
                    <span>{format(new Date(sub.startsAt), "dd/MM/yy")}</span>
                    <span className="text-xs text-neutral-500">
                      → {format(new Date(sub.expiresAt), "dd/MM/yy")}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {getStatusBadge(sub.status)}
                    {sub.autoRenew && (
                      <span className="text-xs text-blue-400">Auto-renew</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-neutral-300">
                  {formatDistanceToNow(new Date(sub.createdAt), {
                    addSuffix: true,

                    locale: es,
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {isBusy && <LoadingOverlay />}

        {!isLoading && subscriptions.length === 0 && (
          <EmptyState
            title="Sin suscripciones"
            description="No hay suscripciones registradas."
            className="m-6"
          />
        )}

        <div className="border-t border-neutral-800 px-4 py-3 text-xs text-neutral-400">
          Total: {subscriptions.length} suscripci
          {subscriptions.length !== 1 ? "ones" : "ón"}
        </div>
      </Card>
    </div>
  );
};

export default SubscriptionsPage;
